export type AppNotification = {
    id: number;
    user_id: number;
    quote_id?: number | null;
    ticket_id?: number | null;
    lead_next_step_id?: number | null;
    kind: string;
    message: string;
    link: string;
    created_at: string;
    read_at: string | null;
};

function apiBase(): string {
    return import.meta.env.VITE_APP_API_BACKEND;
}

function streamPath(): string {
    const base = apiBase();
    const path = base.startsWith('http')
        ? new URL(base).pathname.replace(/\/$/, '')
        : base.replace(/\/$/, '');
    return `${path}/notifications/stream`;
}

// JSON notification endpoints live on the dataProvider (getNotifications,
// markNotificationRead, …). The SSE stream below stays here: it is a long-lived
// streaming fetch, not a JSON request/response, so it does not fit httpClient.

type StreamHandlers = {
    onMessage: (payload: AppNotification) => void;
    onReconnect: () => void | Promise<void>;
};

/**
 * Bearer-authenticated SSE via fetch (no query token).
 * Reconnects after drop and calls onReconnect so the client can refresh.
 */
export function subscribeNotificationStream(
    token: string,
    handlers: StreamHandlers
): () => void {
    const controller = new AbortController();
    let stopped = false;

    const sleep = (ms: number) =>
        new Promise<void>(resolve => setTimeout(resolve, ms));

    const run = async () => {
        while (!stopped && !controller.signal.aborted) {
            try {
                const res = await fetch(streamPath(), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'text/event-stream',
                    },
                    signal: controller.signal,
                });
                if (!res.ok || !res.body) {
                    throw new Error(`SSE HTTP ${res.status}`);
                }

                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (!stopped) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const chunks = buffer.split('\n\n');
                    buffer = chunks.pop() ?? '';
                    for (const chunk of chunks) {
                        const dataLine = chunk
                            .split('\n')
                            .find(line => line.startsWith('data: '));
                        if (!dataLine) continue;
                        try {
                            const payload = JSON.parse(
                                dataLine.slice(6)
                            ) as AppNotification;
                            handlers.onMessage(payload);
                        } catch {
                            // Ignore malformed event payloads.
                        }
                    }
                }
            } catch {
                if (stopped || controller.signal.aborted) break;
            }

            if (stopped || controller.signal.aborted) break;
            try {
                await handlers.onReconnect();
            } catch {
                // Refresh is best-effort.
            }
            await sleep(2000);
        }
    };

    void run();

    return () => {
        stopped = true;
        controller.abort();
    };
}
